import { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import type { CreateLogbookEntryDTO, LogbookEntryType, LogbookPriority } from '@yawi/types';
import { useI18n } from '@/shared/i18n';

interface LogbookEntryFormProps {
  shiftId?: string;
  onSubmit: (data: CreateLogbookEntryDTO) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

const ENTRY_TYPES: LogbookEntryType[] = ['NOVEDAD', 'INCIDENTE', 'OBSERVACION'];
const PRIORITIES: LogbookPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

export const LogbookEntryForm = ({ shiftId, onSubmit, onCancel, loading }: LogbookEntryFormProps) => {
  const { t } = useI18n();
  const [formData, setFormData] = useState<CreateLogbookEntryDTO>({
    type: 'NOVEDAD',
    title: '',
    description: '',
    priority: 'MEDIUM',
    location: '',
    shiftId,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) return;
    await onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-800">{t('logbook.newEntry')}</h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('logbook.entryType')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {ENTRY_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type }))}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.type === type
                      ? 'bg-primary-600 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {t(`logbook.type.${type.toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('logbook.priority.label')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRIORITIES.map((priority) => (
                <button
                  key={priority}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, priority }))}
                  className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                    formData.priority === priority
                      ? priority === 'CRITICAL'
                        ? 'bg-red-600 text-white'
                        : priority === 'HIGH'
                        ? 'bg-amber-500 text-white'
                        : priority === 'MEDIUM'
                        ? 'bg-blue-500 text-white'
                        : 'bg-slate-500 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {t(`logbook.priority.${priority.toLowerCase()}`)}
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.title')}
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('logbook.titlePlaceholder')}
              required
              maxLength={255}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.description')}
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              placeholder={t('logbook.descriptionPlaceholder')}
              rows={4}
              required
              maxLength={2000}
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              {t('common.location')} ({t('common.optional')})
            </label>
            <input
              type="text"
              value={formData.location || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('logbook.locationPlaceholder')}
              maxLength={255}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              disabled={loading}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !formData.title.trim() || !formData.description.trim()}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              {t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
